/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { ArrowLeft, Users, ShieldCheck } from 'lucide-react';
import { UsersTabContent } from './UsersTabContent';

const TABS = [
  { id: 'USERS', label: 'Users', icon: Users },
];

export const RegionalAdminConsole: React.FC = () => {
  const { setCurrentView } = useWBL();
  const [activeTab, setActiveTab] = useState<'USERS'>(() => {
    const stored = sessionStorage.getItem('regionalAdminConsoleTab');
    if (stored === 'USERS') {
      return stored;
    }
    return 'USERS';
  });

  useEffect(() => {
    sessionStorage.setItem('regionalAdminConsoleTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const handlePopState = () => {
      const storedTab = sessionStorage.getItem('regionalAdminConsoleTab');
      if (storedTab === 'USERS') {
        setActiveTab(storedTab);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div className="min-h-screen bg-[#f0f4f8] text-[#011d3c] font-sans pb-16">
      {/* Console Top Header Banner with Back Navigation and Title */}
      <div className="bg-white border-b border-slate-200/80 py-4 px-4 sm:px-6">
        <div className="w-[95%] max-w-[1920px] mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="p-2 hover:bg-slate-100 rounded-xl transition-all cursor-pointer text-slate-500 hover:text-slate-900 group"
              title="Return to Dashboard"
              id="regional-console-back-btn"
            >
              <ArrowLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="flex-1 flex items-center justify-between">
              <div>
                <h1 className="text-xl font-black text-[#011d3c] tracking-tight uppercase">
                  Regional Admin Console
                </h1>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                  Region-specific Directory & Accountability Hub
                </p>
              </div>
              <span className="bg-[#0094c8]/5 border border-[#0094c8]/20 text-[#0094c8] text-[10px] font-black uppercase px-2.5 py-1 rounded-full flex items-center space-x-1 font-mono">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Regional Authority</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="w-[95%] max-w-[1920px] mx-auto px-2 sm:px-4 mt-5 space-y-4">
        
        {/* Mockup Tab Ribbon Row */}
        <div className="bg-[#e2e8f0]/60 p-1 border border-slate-200/50 rounded-2xl flex flex-wrap items-center gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5 transition-all cursor-pointer rounded-xl ${
                  isSelected
                    ? 'bg-white text-slate-900 shadow-xs border border-slate-200/40'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/40'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Deck dépendent on Selected Tab */}
        {activeTab === 'USERS' && (
          <UsersTabContent />
        )}
      </div>
    </div>
  );
};
